import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { HoaReturn, VendorReturn } from './clients/rust-api-client';
import { hoaService, vendorService } from './clients/ApiClients';
import { useUser } from './hooks/UserContext';
import { UserType, canCreateVendor } from './constants/UserTypes';
import { getCompanyIdFromToken } from './utils/TokenUtils';
import LoadingSpinner from './LoadingSpinner';
import { useSyncedHoas, useSyncedVendors } from './sync/hooks';
import { apiErrorMessage } from './utils/apiErrors';
import { useCommandPalette } from './shell/CommandPaletteProvider';
import { CreateButton } from './shell/CreateButton';
import {
  DirectoryColumn,
  DirectoryList,
  EMPTY_CELL,
  StatusCell,
} from './shell/DirectoryList';
import { FilterBar } from './violation/linear/FilterBar';
import { ErrorAlert } from './common/Custom/ErrorAlert';
import { VENDOR_STATUSES } from './vendor/linear/vendorMeta';

interface VendorListProps {
  hoaId?: string | null;
  /**
   * The list is the page: it fills the panel edge to edge and scrolls
   * inside itself. Without it — as a tab on an association — it sits in a
   * bordered box, like the association's homes.
   */
  fill?: boolean;
}

const statusLabel = (status: string) =>
  VENDOR_STATUSES.find((s) => s.value === status.toLowerCase())?.label ??
  status.charAt(0).toUpperCase() + status.slice(1);

const isActive = (vendor: VendorReturn) =>
  vendor.status.toLowerCase() === 'active';

/**
 * Vendors belong to an association, and the fixtures (and real life)
 * put the same landscaper in several of them. On the company-wide list
 * three identical "Green Thumb Landscaping" rows read as duplicates or
 * a leak unless the association is in the row — so it leads, sortable,
 * whenever the list is not already one association's own.
 */
const associationColumn = (
  hoaName: (id: string) => string
): DirectoryColumn<VendorReturn> => ({
  key: 'association',
  label: 'Association',
  className: 'w-48 hidden sm:block',
  render: (vendor) => hoaName(vendor.hoa_id) || EMPTY_CELL,
  sortValue: (vendor) => hoaName(vendor.hoa_id),
});

const COLUMNS: DirectoryColumn<VendorReturn>[] = [
  {
    key: 'services',
    label: 'Services',
    className: 'w-52 hidden md:block',
    render: (vendor) => vendor.service_types || EMPTY_CELL,
    sortValue: (vendor) => vendor.service_types ?? '',
  },
  {
    key: 'contact',
    label: 'Contact',
    className: 'w-48 hidden lg:block',
    render: (vendor) => vendor.contact_name || vendor.email || EMPTY_CELL,
    sortValue: (vendor) => vendor.contact_name || vendor.email || '',
  },
  {
    key: 'phone',
    label: 'Phone',
    className: 'w-32 hidden xl:block',
    render: (vendor) => vendor.phone || EMPTY_CELL,
  },
  {
    key: 'status',
    label: 'Status',
    className: 'w-24',
    render: (vendor) => (
      <StatusCell
        active={isActive(vendor)}
        label={statusLabel(vendor.status)}
      />
    ),
    sortValue: (vendor) => vendor.status.toLowerCase(),
  },
];

const VendorList: React.FC<VendorListProps> = ({ hoaId, fill = false }) => {
  const navigate = useNavigate();
  const [fetchedVendors, setVendors] = useState<VendorReturn[]>([]);
  // The sync engine's local copy when it has one (#561); fetched otherwise.
  const syncedVendors = useSyncedVendors({ hoaId });
  const vendors = syncedVendors ?? fetchedVendors;
  const haveSynced = syncedVendors !== null;
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statuses, setStatuses] = useState<string[]>([]);
  const { userType } = useUser();
  const { openNewVendor } = useCommandPalette();

  // Association names for the company-wide list: the sync engine's copy
  // when it has one, else the same fetch the sidebar makes. Only when the
  // list spans associations; an association's own tab has no use for it.
  const syncedHoas = useSyncedHoas();
  const [fetchedHoas, setFetchedHoas] = useState<HoaReturn[]>([]);
  const hoas = syncedHoas ?? fetchedHoas;
  useEffect(() => {
    if (hoaId || syncedHoas !== null) return;
    let cancelled = false;
    const companyId = getCompanyIdFromToken();
    const request =
      userType === UserType.SUPER_ADMIN
        ? hoaService.getAllHoas()
        : companyId
          ? hoaService.getHoasByCompany({ companyId })
          : null;
    request
      ?.then((r) => {
        if (!cancelled) setFetchedHoas(r.data);
      })
      .catch(() => {
        // The column shows a dash; the list itself is unaffected.
      });
    return () => {
      cancelled = true;
    };
  }, [hoaId, syncedHoas, userType]);

  useEffect(() => {
    const fetchVendors = async () => {
      if (haveSynced) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        let response;

        if (hoaId) {
          response = await vendorService.getVendorsByHoa({ hoaId });
        } else {
          response = await vendorService.getVendorsForUser();
        }

        setVendors(response.data.vendors);
        setFetchError(null);
      } catch (err) {
        console.error('Error fetching vendors:', err);
        setFetchError(apiErrorMessage(err, "Couldn't load vendors."));
      } finally {
        setLoading(false);
      }
    };

    fetchVendors();
  }, [hoaId, haveSynced]);

  const hoaNameOf = useMemo(() => {
    const names = new Map(hoas.map((h) => [h.id, h.name]));
    return (id: string) => names.get(id) ?? '';
  }, [hoas]);

  const columns = useMemo(
    () => (hoaId ? COLUMNS : [associationColumn(hoaNameOf), ...COLUMNS]),
    [hoaId, hoaNameOf]
  );

  const filteredVendors = useMemo(() => {
    const search = searchTerm.toLowerCase();
    return vendors.filter((vendor) => {
      if (statuses.length && !statuses.includes(vendor.status.toLowerCase())) {
        return false;
      }
      return (
        vendor.name.toLowerCase().includes(search) ||
        hoaNameOf(vendor.hoa_id).toLowerCase().includes(search) ||
        (vendor.contact_name &&
          vendor.contact_name.toLowerCase().includes(search)) ||
        (vendor.service_types &&
          vendor.service_types.toLowerCase().includes(search)) ||
        (vendor.email && vendor.email.toLowerCase().includes(search))
      );
    });
  }, [vendors, searchTerm, statuses, hoaNameOf]);

  // The shared create modal (#665), mounted in CommandPaletteProvider. An
  // HOA's own list fixes the association; the all-vendors list leaves it
  // to pick. The new vendor joins this list rather than being opened.
  const handleCreateVendor = () =>
    openNewVendor({
      ...(hoaId ? { scope: { hoaId }, lockScope: true } : {}),
      onCreated: (created) =>
        setVendors((prevVendors) => [...prevVendors, created]),
    });

  // With a synced copy there is nothing to wait for: render it on the
  // first paint, not after the effect clears `loading` (#635).
  if (loading && !haveSynced) {
    return <LoadingSpinner></LoadingSpinner>;
  }

  const filtered = searchTerm !== '' || statuses.length > 0;

  const list = (
    <>
      <FilterBar
        fields={[
          {
            field: 'Status',
            options: VENDOR_STATUSES.map((s) => ({ ...s })),
            values: statuses,
            onChange: setStatuses,
          },
        ]}
        search={searchTerm}
        onSearch={setSearchTerm}
        searchLabel="Search vendors by name, association, contact or service"
        onClearAll={() => setStatuses([])}
        shown={filteredVendors.length}
        total={vendors.length}
        trailing={
          canCreateVendor(userType) && (
            <CreateButton label="New vendor" onClick={handleCreateVendor} />
          )
        }
      />
      <DirectoryList
        items={filteredVendors}
        getKey={(vendor) => vendor.id}
        getName={(vendor) => vendor.name}
        isMuted={(vendor) => !isActive(vendor)}
        columns={columns}
        onOpen={(vendor) => navigate(`/vendors/${vendor.id}`)}
        emptyMessage={
          filtered ? 'No vendors match these filters.' : 'No vendors yet.'
        }
      />
    </>
  );

  const error = fetchError && (
    <ErrorAlert
      title="Couldn't load vendors"
      onDismiss={() => setFetchError(null)}
    >
      {fetchError}
    </ErrorAlert>
  );

  if (fill) {
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        {/* Flush against the panel's edges, the alert needs its own inset. */}
        <div className="px-3 pt-3 empty:hidden">{error}</div>
        {list}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error}
      <div className="overflow-hidden rounded-lg border border-line bg-raised">
        {list}
      </div>
    </div>
  );
};

export default VendorList;
